<template>
  <div class="stacked-stock-info">
    <h3 class="text-lg font-medium mb-3">{{ title }}</h3>
    
    <div v-if="hasUnlimitedStock" class="bg-blue-50 p-3 rounded text-blue-700 mb-4">
      {{ unlimitedStockMessage }}
    </div>
    
    <div v-if="Object.keys(groupedStocks).length > 0">
      <el-collapse>
        <el-collapse-item v-for="(locationStocks, locationName) in groupedStocks" :key="locationName">
          <template #title="{ isActive }">
            <div class="mx-2 flex items-center justify-between w-full">
              <div>{{ locationName }}</div>
              <div>{{ getTotalQuantityForLocation(locationStocks) }}</div>
            </div>
          </template>
          
          <div v-for="(variantGroup, variantName) in groupVariantsByName(locationStocks)" :key="variantName" class="mb-2">
            <div class="variant-header bg-gray-50 p-2 rounded-t font-medium border-l-4 border-blue-500">
              {{ variantName }}
            </div>
            
            <div v-for="stock in variantGroup" :key="stock.id" class="border border-gray-200 m-1">
              <div class="p-2 flex flex-col w-full">
                <div v-if="stock.extraProperties?.batchNumber" class="flex items-center justify-between">
                  <div class="text-gray-700">{{ getBatchNumberLabel() }}</div>
                  <div class="font-bold">
                    <BatchInput :stock="stock" :value="stock.extraProperties?.batchNumber" type="text" property="batchNumber" />
                  </div>
                </div>
                <div v-if="stock.extraProperties?.expiryDate" class="flex items-center justify-between">
                  <div class="text-gray-700">{{ getExpiryDateLabel() }}</div>
                  <div class="font-bold">
                    <BatchInput :stock="stock" :value="stock.extraProperties?.expiryDate" type="date" property="expiryDate" />
                  </div>
                </div>
                <div v-if="stock.extraProperties?.manufactureDate" class="flex items-center justify-between">
                  <div class="text-gray-700">{{ getManufactureDateLabel() }}</div>
                  <div class="font-bold">
                    <BatchInput :stock="stock" :value="stock.extraProperties?.manufactureDate" type="date" property="manufactureDate" />
                  </div>
                </div>
                <div class="flex items-center justify-between">
                  <div class="text-gray-700">{{ getQuantityLabel() }}</div>
                  <div class="font-bold" :class="{'text-red-500': stock.quantity <= 0}">
                    {{ stock.quantity }}
                  </div>
                </div>
                <div v-if="showPendingQuantity" class="flex items-center justify-between">
                  <div class="text-gray-700">{{ getPendingQuantityLabel() }}</div>
                  <div class="font-bold">
                    {{ stock.pendingQuantity || 0 }}
                  </div>
                </div>
              </div>
              <div v-if="stock.itemSerials && stock.itemSerials.length > 0" class="px-2 w-full">
                {{ getSerialsLabel() }}
                <li v-for="(serial, i) in stock.itemSerials" :key="i">
                  <el-tag :key="i" type="info">
                    <SerialInput :serial="serial" />
                  </el-tag>
                </li>
              </div>
            </div>
          </div>
        </el-collapse-item>
      </el-collapse>
    </div>
    
    <div v-if="!hasUnlimitedStock && Object.keys(groupedStocks).length === 0" class="text-gray-500 italic">
      No stock information available
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue'
import Stock from '@/models/inventory/Stock'
import Item from '@/models/inventory/Item'
import BatchInput from '@/views/inventory/items/BatchInput.vue'
import SerialInput from '@/views/inventory/items/SerialInput.vue'

// Define interfaces for locations and variant options
interface Location {
  enName?: string
  arName?: string
  [key: string]: any
}

interface VariantOptions {
  [key: string]: string | undefined
}

// Define the extended stock type with variant name
interface StockDisplay {
  quantity: number
  location: Location
  variantName: string
  [key: string]: any
}

// Define grouped stocks by location
interface GroupedStocks {
  [locationName: string]: StockDisplay[]
}

// Define variant structure
interface VariantWithStocks {
  name?: string
  options?: VariantOptions
  itemStocks?: Stock[]
  unlimitedStock?: boolean
}

export default defineComponent({
  name: 'StackedItemStockDisplay',
  
  components: {
    BatchInput,
    SerialInput
  },
  
  props: {
    stocks: {
      type: Array as PropType<Array<Stock>>,
      required: true
    },
    variants: {
      type: Array as PropType<Array<VariantWithStocks>>,
      default: () => []
    },
    title: {
      type: String,
      default: 'Stock Info'
    },
    unlimitedStock: {
      type: Boolean,
      default: false
    },
    unlimitedStockMessage: {
      type: String,
      default: 'Unlimited Stock (Service or Digital Item)'
    },
    batchNumberLabel: {
      type: String,
      default: 'Batch Number'
    },
    expiryDateLabel: {
      type: String,
      default: 'Expiry Date'
    },
    manufactureDateLabel: {
      type: String,
      default: 'Manufacture Date'
    },
    quantityLabel: {
      type: String,
      default: 'Quantity'
    },
    serialsLabel: {
      type: String,
      default: 'Serial Numbers'
    },
    showPendingQuantity: {
      type: Boolean,
      default: false
    },
    pendingQuantityLabel: {
      type: String,
      default: 'Pending Quantity'
    }
  },
  
  setup(props) {
    // Check if any of the variants or main item has unlimited stock
    const hasUnlimitedStock = computed(() => {
      if (props.unlimitedStock) return true;
      
      // Check variants
      if (props.variants && props.variants.length > 0) {
        return props.variants.some(variant => variant.unlimitedStock);
      }
      
      return false;
    });
    
    // Group stocks by location
    const groupedStocks = computed(() => {
      const grouped: GroupedStocks = {}
      
      // Process the stocks
      props.stocks.forEach(stock => {
        const locationName = (stock.location as Location)?.enName || 
                            (stock.location as Location)?.arName || 
                            'Unknown Location'
        
        if (!grouped[locationName]) {
          grouped[locationName] = []
        }
        
        grouped[locationName].push({
          ...stock,
          variantName: 'Default' // For non-variant items
        } as StockDisplay)
      })
      
      // Process variants if provided
      if (props.variants && props.variants.length > 0) {
        props.variants.forEach((variant: VariantWithStocks) => {
          if (variant.itemStocks && !variant.unlimitedStock) {
            variant.itemStocks.forEach((stock: Stock) => {
              const locationName = (stock.location as Location)?.enName || 
                                  (stock.location as Location)?.arName || 
                                  'Unknown Location'
              
              if (!grouped[locationName]) {
                grouped[locationName] = []
              }
              
              grouped[locationName].push({
                ...stock,
                variantName: variant.name || 
                            `${variant.options?.size || ''} ${variant.options?.color || ''}`.trim()
              } as StockDisplay)
            })
          }
        })
      }
      
      return grouped
    })

    // Helper to group stocks by variant name
    const groupVariantsByName = (stocks: StockDisplay[]) => {
      const grouped: { [key: string]: StockDisplay[] } = {};
      stocks.forEach(stock => {
        const variantName = stock.variantName;
        if (!grouped[variantName]) {
          grouped[variantName] = [];
        }
        grouped[variantName].push(stock);
      });
      return grouped;
    };

    // Helper to get total quantity for a location
    const getTotalQuantityForLocation = (locationStocks: StockDisplay[]) => {
      return locationStocks.reduce((sum, stock) => sum + stock.quantity, 0);
    };

         // Helper for labels
     const getBatchNumberLabel = () => props.batchNumberLabel;
     const getExpiryDateLabel = () => props.expiryDateLabel;
     const getManufactureDateLabel = () => props.manufactureDateLabel;
     const getQuantityLabel = () => props.quantityLabel;
     const getSerialsLabel = () => props.serialsLabel;
     const getPendingQuantityLabel = () => props.pendingQuantityLabel;
    
    return {
      groupedStocks,
      hasUnlimitedStock,
      groupVariantsByName,
      getTotalQuantityForLocation,
      getBatchNumberLabel,
      getExpiryDateLabel,
      getManufactureDateLabel,
      getQuantityLabel,
      getSerialsLabel,
      getPendingQuantityLabel
    }
  }
})
</script>

<style scoped>
.stacked-stock-info {
  max-width: 100%;
}
</style> 